import React, { Component } from 'react';
import axios from "axios";
import CircularProgress from '@mui/material/CircularProgress';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { WithStyles, withStyles } from '@mui/styles';
import { isEqual, isEmpty } from 'lodash';

// Contexts
import { withTranslate } from '../../../core/TenantProvider';
import { getConfigProperty } from '../../../core/EnvironmentConfig/EnvironmentConfig';
// Components - Atoms, Molecules, Organisms, Pages
import APButton from '../../atoms/APButton';
import APInput from '../../atoms/APInput';
import APLabel from '../../atoms/APLabel';
import APSelect from '../../atoms/APSelect';
import APHeading from './../../atoms/APHeading';
import APAlertMessage from '../../atoms/APAlertMessage';

import APBankAccountLookupStyles from './styles/APBankAccountLookup.style';
//Redux
import { ReducerState } from '../../../redux/modules';
// Utils
import GetBasicDetails from '../../../core/utils/GetBasicDetails';
import { getCountryISO2 } from './utils/isoMapper';

const {
	basicAuthToken: BASIC_AUTH_TOKEN,
	organisationCode: ORGANISATION
} = GetBasicDetails();

interface APBankAccountLookupProps extends WithStyles<typeof APBankAccountLookupStyles> {
	label?: Function,
	onBlur?: Function,
	onFocus?: Function,
	onChange?: Function,
	name?: string,
	search?: any,
	settings: any,
	translate: Function,
	value: any,
}

class APBankAccountLookup extends Component<APBankAccountLookupProps, {}> {

	static getDerivedStateFromProps(props: APBankAccountLookupProps, state: any) {
		const shouldStateUpdate =
			props.value.swift_code !== '' &&
			props.value.bank_name !== '' &&
			props.value.bank_address !== '';

		if (shouldStateUpdate) {
			return {
				...state,
				account: props.value
			};
		}

		return state;
	}

	state = {
		account: {
			name: '',
			bank_account_name: '',
			payment_country: '',
			payment_currency: '',
			account_number: '',
			sort_code: '',
			bsb_number: '',
			swift_code: '',
			transit_number: '',
			aba_number: '',
			branch_code: '',
			ifsc_code: '',
			iban: '',
			cnaps_code: '',
			bank_name: '',
			bank_address: '',
			matrixFields: []
		},
		isFocused: false,
		isEditing: false,
		isValidBankDetailFields: false,
		isLoading: false,
		isError: false,
		bankResult: {},
		accNameError: false,
		accCountryError: false,
		accCurrencyError: false,
		accBankError: false,
		accAddrError: false,
		accNoError: false
	};

	constructor(props: APBankAccountLookupProps) {
		super(props);

		this.onBlur = this.onBlur.bind(this);
		this.onFocus = this.onFocus.bind(this);
		this.onManualChange = this.onManualChange.bind(this);
		this.onChangeCountry = this.onChangeCountry.bind(this);
		this.onManualEntryClick = this.onManualEntryClick.bind(this);
		this.onRetrieveClick = this.onRetrieveClick.bind(this);
	}

	getBankDetailsAFEndPoint: string = getConfigProperty('apiEndPoint')['getBankDetails'];
	accessToken = BASIC_AUTH_TOKEN;
	axiosDefaults: any = {
		"baseUrl": "",
		"headers": {
			"Access-Control-Allow-Origin": "*",
			"Authorization": `Basic ${this.accessToken}`,
			"Content-Type": "application/json"
		}
	};

	onRetrieveClick() {

		const { name, onChange, value } = this.props;
		const { account } = this.state;

		const countryCode = getCountryISO2(account['payment_country']);
		let selectedFields: any = {
			"organisation": ORGANISATION,
			"country": countryCode
		};

		account.matrixFields.forEach((field: any) => {
			let fieldValue: any = value[field];

			if (fieldValue && field !== 'account_number') {

				selectedFields[field] = fieldValue;
			}
		});

		this.setState({ isLoading: true, isError: false }, () => {

			axios.defaults.baseURL = this.axiosDefaults.baseURL;
			axios.defaults.headers.post = this.axiosDefaults.headers;
			axios
				.post(this.getBankDetailsAFEndPoint, selectedFields)
				.then((response) => {
					const bankResult = response.data;

					if (bankResult.code === "AF0001") {
						this.setState({ isLoading: false, isError: true, isEditing: true }, () => {
							console.log(bankResult.description);
						});
					} else {
						const accountInfo: any = {
							...this.state.account,
							bank_name: bankResult.bank_name,
							bank_address: bankResult.address,
							swift_code: bankResult.recommended_bic
						};

						this.setState({ isLoading: false, bankResult, account: accountInfo }, () => {
							if (onChange) {
								onChange({
									target: {
										name,
										value: this.state.account
									}
								});
							}
						});
					}
				})
				.catch((error) => {
					if (error.status !== 200) {
						this.setState({ isLoading: false, isError: true, isEditing: true }, () => {
							console.log(error + ' Error got here');
						});
					}
				});
		});
	}

	onBlur(evt: any) {
		const { onBlur, name } = this.props;

		const fieldName = evt.target.name;
		let blurState: any = { isFocused: false };
		const fieldError = (this.isFieldValid(evt.target.name)) ? false : true

		switch (fieldName) {
			case 'bank_account_name':
				blurState.accNameError = fieldError;
				break;

			case 'payment_country':
				blurState.accCountryError = fieldError;
				break;

			case 'payment_currency':
				blurState.accCurrencyError = fieldError;
				break;

			case 'bank_name':
				blurState.accBankError = fieldError;
				break;

			case 'bank_address':
				blurState.accAddrError = fieldError;
				break;

			case 'account_number':
				blurState.accNoError = fieldError;
				break;
		}

		this.setState(blurState, () => {
			if (onBlur) {
				onBlur({
					target: {
						name
					}
				});
			}
		});
	}

	onFocus(evt: any) {
		const { onFocus } = this.props;
		this.setState({
			isFocused: true
		}, () => {
			if (onFocus) {
				onFocus(evt);
			}
		});
	}

	codeMatrix(evt: any) {

		const { target: { name, value } } = evt;
		const defaultFields = ["account_number", "swift_code"];

		const fieldsMatrix = [
			{ type: 'account_number', values: ["AUS", "NZL", "CAN", "USA", "ZAF", "IND", "CHN"] },
			{ type: 'bsb_number', values: ["AUS", "NZL"] },
			{ type: 'swift_code', values: ["AUS", "NZL", "CAN", "", "USA", "ZAF", "IND", "CHN", "QAT", "BGR", "GGY", "JEY"] },
			{ type: 'transit_number', values: ["CAN"] },
			{ type: 'aba_number', values: ["USA"] },
			{ type: 'branch_code', values: ["ZAF"] },
			{ type: 'ifsc_code', values: ["IND"] },
			{ type: 'iban', values: ["AND", "AUT", "BH", "BEL", "HRV", "CYP", "CZE", "DNK", "EST", "FIN", "FRA", "GIB", "DEU", "GRC", "HUN", "ISL", "IRL", "ISR", "ITA", "KWT", "LVA", "LTU", "LIE", "LUX", "MLT", "MCO", "MNE", "NLD", "NOR", "POL", "PRT", "ROU", "SAU", "SVK", "SWE", "ESP", "CHE", "TUN", "TUR", "ARE", "GBR", "QAT", "BGR", "GGY", "JEY"] },
			{ type: 'cnaps_code', values: ["CHN"] }
		]

		let selectedFields: any = [];
		let account: any;

		const val = (name === 'payment_currency') ? this.state.account.payment_country : value;
		fieldsMatrix.forEach(obj => {
			if (obj['values'].includes(val)) {
				selectedFields.push(obj.type);
			}
		});

		if ((name === 'payment_currency' && value === 'GBP' && this.state.account.payment_country === 'GBR') ||
			(name === 'payment_country' && value === 'GBR' && this.state.account.payment_currency === 'GBP')) {
			account = {
				...this.state.account,
				[name]: value,
				account_number: '', sort_code: '', bsb_number: '', swift_code: '', transit_number: '', aba_number: '', branch_code: '', ifsc_code: '', iban: '', cnaps_code: '', // Reset these
				matrixFields: ['account_number', 'sort_code'],
				bank_name: '',
				bank_address: ''
			}
		} else {

			if (isEmpty(selectedFields) ||
				(name === 'payment_currency' && value === '') ||
				(name === 'payment_country' && value === '')) {
				selectedFields = defaultFields;
			}

			account = {
				...this.state.account,
				[name]: value,
				account_number: '', sort_code: '', bsb_number: '', swift_code: '', transit_number: '', aba_number: '', branch_code: '', ifsc_code: '', iban: '', cnaps_code: '', // Reset these
				matrixFields: isEmpty(selectedFields) ? this.state.account.matrixFields : selectedFields,
				bank_name: '',
				bank_address: ''
			};
		}

		this.setState({
			account
		}, () => {
			const { name: nameProp, onChange } = this.props;
			if (onChange) {
				onChange({
					target: {
						name: nameProp,
						value: account
					}
				});
			}
		});

	}

	onManualChange(evt: any) {

		const { target: { name, value } } = evt;
		const account: any = {
			...this.state.account,
			[name]: value
		};

		this.setState({
			account
		}, () => {
			const { name: fieldName, onChange } = this.props;
			if (onChange) {
				onChange({
					target: {
						name: fieldName,
						value: account
					}
				});
			}
		});
	}

	onChangeCountry(evt: any) {

		const { target: { name, value } } = evt;

		if (this.state.isEditing) {
			const account: any = {
				...this.state.account,
				[name]: value,
			};

			this.setState({
				isError: false, // Reset form
				isEditing: false, // Reset form
				account,
			}, () => {
				const { name: fieldName, onChange } = this.props;
				if (onChange) {
					onChange({
						target: {
							name: fieldName,
							value: account
						}
					});
				}
			});
		}

		this.codeMatrix(evt);
	}

	isFieldValid(field: any) {

		let value: any = this.props.value[field];

		let isValid = true;

		if (!value) {
			isValid = false;
		}

		if (isValid && value && field.validation) {
			isValid = Boolean(String(value).match(field.validation));
		}

		return isValid;
	}

	validate() {

		const { account } = this.state
		let isValid: boolean = true;

		account.matrixFields.forEach((field: any) => {
			if (isValid) {
				isValid = this.isFieldValid(field);
			}
		});

		// OR combinations for valid fields - this is sooooo ugly....
		if ((account.payment_country === 'AUS' || account.payment_country === 'NZL') // AUD, NZL = bsb_number or swift_code
			&& ((account['bsb_number'] || account['swift_code']) && account['account_number'])) {
			isValid = true;
		}

		if ((account.payment_country === 'CAN') // CAN = swift_code or transit_number
			&& ((account['transit_number'] || account['swift_code']) && account['account_number'])) {
			isValid = true;
		}

		if ((account.payment_country === 'USA') // USA = swift_code or aba_number
			&& ((account['aba_number'] || account['swift_code']) && account['account_number'])) {
			isValid = true;
		}

		if ((account.payment_country === 'ZAF') // ZAF = swift_code or branch_code
			&& ((account['branch_code'] || account['swift_code']) && account['account_number'])) {
			isValid = true;
		}

		if ((account.payment_country === 'IND') // IND = swift_code or ifsc_code
			&& ((account['ifsc_code'] || account['swift_code']) && account['account_number'])) {
			isValid = true;
		}

		if ((account.payment_country === 'CHN') // CHN = swift_code or ifsc_code
			&& ((account['cnaps_code'] || account['swift_code']) && account['account_number'])) {
			isValid = true;
		}

		if ((account.payment_country === 'GGY' || account.payment_country === 'JEY' || account.payment_country === 'BGR' || account.payment_country === 'QAT')
			&& (account['iban'] || account['swift_code'])) {
			isValid = true;
		}

		this.setState({
			isValidBankDetailFields: isValid
		});
	}

	onManualEntryClick() {

		this.setState({
			isEditing: true,
			bankResult: {}
		})
	}

	componentDidUpdate(prevProps: APBankAccountLookupProps, prevState) {

		const shouldValidate = (
			!isEqual(prevProps.value, this.props.value) ||
			!isEqual(prevState.isValidBankDetailFields, this.state.isValidBankDetailFields) ||
			!isEqual(prevState.account.matrixFields, this.state.account.matrixFields)
		);

		if (shouldValidate) {
			this.validate();
		}
	}

	render() {
		const { classes, translate } = this.props;
		const { isEditing, isValidBankDetailFields, isLoading, isError,
			accNameError, accCurrencyError, accCountryError, accBankError, accAddrError, accNoError
		} = this.state;

		const account: any = this.state.account || {};

		return (
			<div className={classes.fieldsContainer}>

				<APInput className={classes.field} label={translate('bank.account.lookup.name.label')} name="bank_account_name"
					onBlur={this.onBlur}
					onChange={this.onManualChange}
					onFocus={this.onFocus}
					value={account.bank_account_name || ''}
					inputProps={{ maxLength: 41 }}
					error={((account.bank_account_name && account.bank_account_name.length > 40) || (accNameError && !account.bank_account_name && account.bank_account_name.length === 0)) ? true : false}
					helperText={(account.bank_account_name && account.bank_account_name.length > 40) ? translate('bank.account.lookup.name.label.max.error') : ''}
				/>

				<APSelect className={classes.field} label={translate('bank.account.lookup.country.label')} name="payment_country"
					onBlur={this.onBlur}
					onChange={this.onChangeCountry}
					type="country"
					value={account.payment_country || ''}
					error={accCountryError}
				/>

				<APSelect className={classes.field} label={translate('bank.account.lookup.currency.label')} name="payment_currency"
					onBlur={this.onBlur}
					onChange={this.onChangeCountry}
					type="currency"
					value={account.payment_currency || ''}
					error={accCurrencyError}
				/>

				{account.payment_country && account.payment_currency &&
					<APHeading className={classes.heading} variant="body1">
						{translate('bank.account.lookup.bankdetails.heading')}
					</APHeading>
				}

				{isError &&
					<APAlertMessage className={`errorMessage`} message={translate("bank.account.lookup.bankdetails.alert.message", {
						link: 'skip this step'
					})} title={translate("bank.account.lookup.bankdetails.alert.title")} severity={"error"} icon={'error'} hideCloseIcon={true} />
				}

				{account.matrixFields && account.payment_country && account.payment_currency &&
					account.matrixFields.map((input, index) =>
					(`${input}` === 'account_number' ?

						(<APInput key={`${input}_${index}`} className={classes.field} label={translate(`bank.account.lookup.${input}.label`)} name={input}
							onBlur={this.onBlur}
							onChange={this.onManualChange}
							onFocus={this.onFocus}
							value={account[input] || ''}
							error={((account.account_number && account.account_number.length > 20) || (accNoError && !account.account_number && account.account_number.length === 0)) ? true : false}
							helperText={(account.account_number && account.account_number.length > 20) ? translate('bank.account.lookup.number.label.max.error') : ''}
						/>)
						:
						(<APInput key={`${input}_${index}`} className={classes.field} label={translate(`bank.account.lookup.${input}.label`)} name={input}
							onBlur={this.onBlur}
							onChange={this.onManualChange}
							onFocus={this.onFocus}
							value={account[input] || ''}
						/>)

					)
					)
				}


				{!isEditing && account.matrixFields && account.matrixFields.length > 0 && account.payment_country && account.payment_currency &&
					<>
						<APButton children={translate('bank.account.lookup.retrieve.button')} className={classes.button} color="primary" disabled={!isValidBankDetailFields || isLoading} variant="contained" onClick={this.onRetrieveClick} />
						{isLoading && <CircularProgress className={classes.loadingIcon} size={25} />}
					</>
				}

				{!isEditing && account.matrixFields && account.matrixFields.length > 0 && account.payment_country && account.payment_currency && !account.bank_name && !account.bank_address &&
					<APButton children={translate('bank.account.lookup.manual.link')} className={classes.textButton} onClick={this.onManualEntryClick} color="primary" />
				}


				{/* Selection made */}
				{!isEditing && !isError && account.bank_name && account.bank_address &&
					<div className={classes.selectionSummary}>

						{this.state.account['bank_name'] &&
							<div>
								<APLabel className={classes.summaryLabel} children={translate('bank.account.lookup.summary.name.label')} />
								<APLabel className={classes.summaryText}>{account.bank_name}</APLabel>
							</div>
						}

						{this.state.account['bank_address'] &&
							<div>
								<APLabel className={classes.summaryLabel} children={translate('bank.account.lookup.summary.address.label')} />
								<APLabel className={classes.summaryText}>{account.bank_address.toLowerCase()}</APLabel>
							</div>
						}

						<APButton className={classes.textButton} onClick={this.onManualEntryClick} color="primary">
							{translate('address.lookup.selection.edit.label')}
						</APButton>
					</div>
				}

				{isEditing && account.payment_country && account.payment_currency &&
					<>
						<APInput
							className={classes.field}
							label={translate('bank.account.lookup.bank_name.label')}
							name="bank_name"
							value={this.state.account['bank_name'] || ''}
							onBlur={this.onBlur}
							onChange={this.onManualChange}
							onFocus={this.onFocus}
							inputProps={{ maxLength: 66 }}
							error={((account.bank_name && account.bank_name.length > 65) || (accBankError && !account.bank_name && account.bank_name.length === 0)) ? true : false}
							helperText={(account.bank_name && account.bank_name.length > 65) ? translate('bank.account.lookup.bank_name.label.max.error') : ''}
						/>

						<APInput
							className={classes.field}
							label={translate('bank.account.lookup.bank_address.label')}
							name="bank_address"
							value={this.state.account['bank_address'] || ''}
							onBlur={this.onBlur}
							onChange={this.onManualChange}
							onFocus={this.onFocus}
							inputProps={{ maxLength: 66 }}
							error={((account.bank_address && account.bank_address.length > 65) || (accAddrError && !account.bank_address && account.bank_address.length === 0)) ? true : false}
							helperText={(account.bank_address && account.bank_address.length > 65) ? translate('bank.account.lookup.bank_address.label.max.error') : ''}
						/>
					</>
				}

			</div>
		);
	}
}

const mapStateToProps = (store: ReducerState) => ({
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(
	withTranslate(
		withStyles(APBankAccountLookupStyles)(APBankAccountLookup)
	)
);
